import type { EditorSession, DragState, DropTarget } from "../state/session.js";

export interface DragHandlers {
  resolve(x: number, y: number, drag: DragState): DropTarget | null;
  drop(target: DropTarget, drag: DragState): void;
  autoScroll?(x: number, y: number): void;
}

const handlers = new WeakMap<EditorSession, DragHandlers>();

export function registerDragHandlers(session: EditorSession, h: DragHandlers | null): void {
  if (h) handlers.set(session, h);
  else handlers.delete(session);
}

/**
 * Starts a pointer-driven drag. Call from a pointerdown handler; the drag
 * activates after a small movement threshold so clicks still work.
 */
export function beginDrag(session: EditorSession, e: PointerEvent | React.PointerEvent, initial: Omit<DragState, "x" | "y">, opts: { threshold?: number; immediate?: boolean } = {}): void {
  if (e.button !== 0) return;
  const threshold = opts.threshold ?? 4;
  const startX = e.clientX;
  const startY = e.clientY;
  const doc = (e.target as Element).ownerDocument;
  let active = !!opts.immediate;
  let raf = 0;
  let last: { x: number; y: number } | null = null;
  let scrollTimer: number | null = null;

  const update = () => {
    raf = 0;
    if (!last) return;
    const h = handlers.get(session);
    const drag = { ...initial, x: last.x, y: last.y };
    session.ui.patch({ drag, dropTarget: h ? h.resolve(last.x, last.y, drag) : null });
  };

  const onMove = (ev: PointerEvent) => {
    if (!active) {
      if (Math.hypot(ev.clientX - startX, ev.clientY - startY) < threshold) return;
      active = true;
      doc.body.classList.add("bpe-dragging");
      session.ui.patch({ drag: { ...initial, x: ev.clientX, y: ev.clientY } });
    }
    last = { x: ev.clientX, y: ev.clientY };
    if (!raf) raf = requestAnimationFrame(update);
    if (scrollTimer === null) {
      scrollTimer = window.setInterval(() => {
        if (last) handlers.get(session)?.autoScroll?.(last.x, last.y);
      }, 50);
    }
  };

  const finish = (ev: PointerEvent, cancelled: boolean) => {
    doc.removeEventListener("pointermove", onMove, true);
    doc.removeEventListener("pointerup", onUp, true);
    doc.removeEventListener("pointercancel", onCancel, true);
    doc.removeEventListener("keydown", onKey, true);
    doc.body.classList.remove("bpe-dragging");
    if (raf) cancelAnimationFrame(raf);
    if (scrollTimer !== null) clearInterval(scrollTimer);
    if (!active) return;
    const h = handlers.get(session);
    const drag = { ...initial, x: ev.clientX, y: ev.clientY };
    const target = cancelled ? null : (h ? h.resolve(ev.clientX, ev.clientY, drag) : null);
    session.ui.patch({ drag: null, dropTarget: null });
    if (target && target.valid && h) h.drop(target, drag);
  };
  const onUp = (ev: PointerEvent) => finish(ev, false);
  const onCancel = (ev: PointerEvent) => finish(ev, true);
  const onKey = (ev: KeyboardEvent) => {
    if (ev.key === "Escape") finish(new PointerEvent("pointerup", { clientX: -1, clientY: -1 }), true);
  };

  doc.addEventListener("pointermove", onMove, true);
  doc.addEventListener("pointerup", onUp, true);
  doc.addEventListener("pointercancel", onCancel, true);
  doc.addEventListener("keydown", onKey, true);
  if (opts.immediate) session.ui.patch({ drag: { ...initial, x: startX, y: startY } });
}
